// NFL positions
export type NflPosition = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';

// MLB positions
export type MlbPosition = 'C' | '1B' | '2B' | '3B' | 'SS' | 'OF' | 'SP' | 'RP' | 'DH';

// Combined position type
export type PlayerPosition = NflPosition | MlbPosition;

export interface Player {
  id: string;
  name: string;
  position: PlayerPosition;
  team: string;
  trashPoints: number;
  stats: PlayerStats;
  imageUrl?: string;
}

export interface PlayerStats {
  // NFL stats
  interceptions?: number;
  fumbles?: number;
  runsForLoss?: number;
  missedFieldGoals?: number;
  sacks?: number;
  droppedPasses?: number;
  // MLB batting stats
  strikeouts?: number;
  gidp?: number;
  outs?: number;
  leftOnBase?: number;
  // MLB pitching stats
  earnedRuns?: number;
  walks?: number;
  wildPitches?: number;
  blownSaves?: number;
  hrAllowed?: number;
  balks?: number;
  hitBatters?: number;
  // MLB fielding stats
  errors?: number;
  passedBalls?: number;
  // MLB baserunning stats
  caughtStealing?: number;
  pickedOff?: number;
}

export type LeagueMemberRole = 'commissioner' | 'co-commissioner' | 'player';

export interface LeagueMember {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
  role: LeagueMemberRole;
  joinedAt: Date;
  avatarUrl?: string;
}

export interface Team {
  id: string;
  name: string;
  owner: string;
  ownerId?: string;
  players: Player[];
  weeklyPoints: number;
  totalPoints: number;
  wins: number;
  losses: number;
  sabotagesUsed: number;
  avatarUrl?: string;
  leagueRole?: LeagueMemberRole;
}

export interface Matchup {
  id: string;
  week: number;
  homeTeam: Team;
  awayTeam: Team;
  homeScore: number;
  awayScore: number;
  activeSabotages: Sabotage[];
  status: 'upcoming' | 'live' | 'completed';
}

export interface Sabotage {
  id: string;
  name: string;
  description: string;
  effect: string;
  targetTeamId: string;
  appliedBy: string;
  cost: number;
  icon: string;
}

export type SabotageRarity = 'common' | 'rare' | 'legendary';

export interface SabotageOption {
  id: string;
  name: string;
  description: string;
  effect: string;
  icon: string;
  color: string;
  rarity?: SabotageRarity;
}

export const SABOTAGE_OPTIONS: SabotageOption[] = [
  {
    id: 'wr-100',
    name: 'Century Curse',
    description: 'WR over 100 yards',
    effect: '-10 points',
    icon: '🎯',
    color: 'hsl(0, 84%, 60%)',
    rarity: 'common',
  },
  {
    id: 'qb-300',
    name: 'Arm Breaker',
    description: 'QB over 300 yards',
    effect: '-15 points',
    icon: '💪',
    color: 'hsl(280, 84%, 60%)',
    rarity: 'rare',
  },
  {
    id: 'td-double',
    name: 'TD Trouble',
    description: 'Any 2+ TD game',
    effect: '-20 points',
    icon: '🔥',
    color: 'hsl(25, 95%, 53%)',
    rarity: 'rare',
  },
  {
    id: 'rb-rush',
    name: 'Ground Zero',
    description: 'RB over 80 rush yards',
    effect: '-8 points',
    icon: '🏃',
    color: 'hsl(160, 84%, 39%)',
    rarity: 'common',
  },
  {
    id: 'clean-game',
    name: 'Clean Sweep',
    description: 'No turnovers by team',
    effect: '-25 points',
    icon: '✨',
    color: 'hsl(45, 93%, 47%)',
    rarity: 'legendary',
  },
  {
    id: 'points-freeze',
    name: 'Point Freeze',
    description: 'Highest scorer loses pts',
    effect: 'Points halved',
    icon: '❄️',
    color: 'hsl(200, 84%, 60%)',
    rarity: 'legendary',
  },
  {
    id: 'fumble-bonus',
    name: 'Butter Fingers',
    description: 'Opponent fumble bonus',
    effect: '+5 per fumble',
    icon: '🧈',
    color: 'hsl(35, 84%, 60%)',
    rarity: 'common',
  },
  {
    id: 'int-jackpot',
    name: 'INT Jackpot',
    description: 'Triple INT points',
    effect: '3x INT value',
    icon: '🎰',
    color: 'hsl(320, 84%, 60%)',
    rarity: 'rare',
  },
];

export const MLB_SABOTAGE_OPTIONS: SabotageOption[] = [
  {
    id: 'mlb-balk-call',
    name: 'Balk Call',
    description: '2x BB/BK/HBP for both teams',
    effect: '2x walks/balks/HBP (both teams)',
    icon: '⚾',
    color: 'hsl(25, 95%, 53%)',
    rarity: 'common',
  },
  {
    id: 'mlb-butterfingers',
    name: 'Butterfingers',
    description: '1.5x error points for both teams',
    effect: '1.5x errors (both teams)',
    icon: '🧤',
    color: 'hsl(280, 84%, 60%)',
    rarity: 'common',
  },
  {
    id: 'mlb-fielders-choice',
    name: "Fielder's Choice",
    description: 'SB penalties neutralized for your team',
    effect: 'SB penalty nullified (your team)',
    icon: '🏃',
    color: 'hsl(145, 70%, 45%)',
    rarity: 'common',
  },
  {
    id: 'mlb-pitching-change',
    name: 'Pitching Change',
    description: "Opponent's best pitcher → worst pitcher score",
    effect: 'Best→worst pitcher (opponent)',
    icon: '🔄',
    color: 'hsl(45, 93%, 47%)',
    rarity: 'rare',
  },
  {
    id: 'mlb-k-pocalypse',
    name: 'K-Pocalypse',
    description: '2x batter strikeouts for both teams',
    effect: '2x strikeouts (both teams)',
    icon: '💀',
    color: 'hsl(35, 84%, 60%)',
    rarity: 'rare',
  },
  {
    id: 'mlb-gopher-ball',
    name: 'Gopher Ball',
    description: '2x HR allowed for both teams',
    effect: '2x HR allowed (both teams)',
    icon: '🏠',
    color: 'hsl(200, 84%, 60%)',
    rarity: 'rare',
  },
  {
    id: 'mlb-juiced-balls',
    name: 'Juiced Balls',
    description: '50% less HR/RBI/hit penalties for your team',
    effect: '50% less hit penalties (your team)',
    icon: '💉',
    color: 'hsl(145, 70%, 45%)',
    rarity: 'rare',
  },
  {
    id: 'mlb-seventh-inning',
    name: 'Seventh Inning Curse',
    description: '2x earned runs league-wide',
    effect: '2x earned runs (league-wide)',
    icon: '7️⃣',
    color: 'hsl(320, 84%, 60%)',
    rarity: 'legendary',
  },
  {
    id: 'mlb-error-jackpot',
    name: 'Error Jackpot',
    description: '3x error points league-wide',
    effect: '3x errors (league-wide)',
    icon: '🎰',
    color: 'hsl(0, 84%, 60%)',
    rarity: 'legendary',
  },
  {
    id: 'mlb-closer-curse',
    name: 'Closer Curse',
    description: '3x blown saves league-wide',
    effect: '3x blown saves (league-wide)',
    icon: '🚪',
    color: 'hsl(160, 84%, 39%)',
    rarity: 'legendary',
  },
];
